'use client';

/**
 * 클라이언트 측 진단 로그를 BE(stdout → CloudWatch)로 우회 전송하는 유틸.
 *
 * Datadog RUM은 iframe + storage 차단 환경에서 세션이 만들어지지 않아 이벤트를 전송하지 않는다.
 * fetch는 sessionStorage/localStorage 권한과 무관하므로 storage가 막혀도 동작 → 그 환경의
 * 사용자(la12777 같은 케이스)도 진단 데이터 수집 가능.
 *
 * 검색: CloudWatch Logs `/aws/elasticbeanstalk/Cmong-fe-grs/var/log/web.stdout.log`
 *      → filter-pattern '"[CLIENT_LOG]"' 또는 특정 context 검색
 */

interface ClientLogPayload {
  context: string;
  error?: SerializedError;
  extra?: Record<string, unknown>;
}

interface SerializedError {
  name?: string;
  message?: string;
  stack?: string;
}

// trailingSlash: true 설정으로 '/api/client-log' → '/api/client-log/'로 308 리다이렉트.
// fetch/sendBeacon이 redirect 타다 CORS/method 이슈로 실패할 수 있어 처음부터 slash 포함.
const ENDPOINT = '/api/client-log/';

export function serializeError(err: unknown): SerializedError {
  if (err instanceof Error) {
    return {
      name: err.name,
      message: err.message,
      // 스택이 너무 길면 BE 로그가 시끄러워지므로 상위 10줄만
      stack: err.stack?.split('\n').slice(0, 10).join('\n'),
    };
  }
  return { message: String(err) };
}

function safeStringify(obj: unknown): string {
  try {
    return JSON.stringify(obj);
  } catch {
    return '{}';
  }
}

/**
 * 절대 throw하지 않음. 로깅 함수가 또 에러를 던지면 catch 체인이 무한 발생할 수 있으므로
 * 모든 경로를 try/catch로 감싼다.
 */
export function reportClientLog(payload: ClientLogPayload): void {
  if (typeof window === 'undefined') return;

  const body = safeStringify({
    context: payload.context,
    error: payload.error,
    extra: payload.extra,
    pathname: window.location?.pathname,
    search: window.location?.search,
    referrer: typeof document !== 'undefined' ? document.referrer : undefined,
    inIframe: window.top !== window.self,
  });

  // sendBeacon 우선 — page unload 중에도 전송 보장 + 비동기 fire-and-forget
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      const ok = navigator.sendBeacon(ENDPOINT, blob);
      if (ok) return;
    }
  } catch {
    // fallback으로 흐름
  }

  // fetch fallback (keepalive로 unload 중에도 전송 시도)
  try {
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // 마지막 안전장치 — 로깅은 silent fail
  }
}

interface StorageProbeResult {
  sessionStorage: 'ok' | 'error';
  sessionStorageError?: string;
  localStorage: 'ok' | 'error';
  localStorageError?: string;
  cookie: 'ok' | 'error';
  cookieError?: string;
}

function probeStorage(): StorageProbeResult {
  const result: StorageProbeResult = {
    sessionStorage: 'ok',
    localStorage: 'ok',
    cookie: 'ok',
  };

  try {
    sessionStorage.setItem('__grs_probe', '1');
    sessionStorage.removeItem('__grs_probe');
  } catch (e) {
    result.sessionStorage = 'error';
    result.sessionStorageError = e instanceof Error ? `${e.name}: ${e.message}` : String(e);
  }

  try {
    localStorage.setItem('__grs_probe', '1');
    localStorage.removeItem('__grs_probe');
  } catch (e) {
    result.localStorage = 'error';
    result.localStorageError = e instanceof Error ? `${e.name}: ${e.message}` : String(e);
  }

  // 쿠키는 throw가 아니라 silent fail (write 후 read 못 하면 차단된 것)
  try {
    document.cookie = '__grs_probe=1; path=/; SameSite=None; Secure';
    if (!document.cookie.includes('__grs_probe=1')) {
      result.cookie = 'error';
      result.cookieError = 'cookie write rejected (third-party blocked or SameSite issue)';
    }
    document.cookie = '__grs_probe=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  } catch (e) {
    result.cookie = 'error';
    result.cookieError = e instanceof Error ? `${e.name}: ${e.message}` : String(e);
  }

  return result;
}

/**
 * 매직링크 페이지 진입 시점에 브라우저 storage 실제 동작 여부 검사 후 BE로 전송.
 * 어떤 사용자가 어떤 storage를 못 쓰는지 정확히 파악하기 위함.
 */
export function reportStorageProbe(extra?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  const probe = probeStorage();
  reportClientLog({ context: 'grs-magic-link-storage-probe', extra: { probe, ...extra } });
}
